import {
  campaignIncludesChannel,
  type AdChannel,
  type CampaignChannels,
} from './platformEnabled';

// Pure helpers for the campaign "Channels" selector (2026-10-24). The gate itself
// lives in platformEnabled.ts; this is the authoring side.

export const CHANNEL_OPTIONS: Array<{ value: CampaignChannels; label: string }> = [
  { value: 'both', label: 'Both (default)' },
  { value: 'meta', label: 'Meta only' },
  { value: 'google', label: 'Google only' },
];

// Short badge text for lists / detail. Absent reads as Both.
export function channelsBadgeLabel(channels: CampaignChannels | null | undefined): string {
  if (channels === 'meta') return 'Meta only';
  if (channels === 'google') return 'Google only';
  return 'Meta + Google';
}

export function channelsList(channels: CampaignChannels | null | undefined): AdChannel[] {
  return (['meta', 'google'] as const).filter((c) => campaignIncludesChannel(channels, c));
}

// UTM platforms follow the campaign's channels. Keeps the platforms the AM has
// ticked that the campaign still includes; with none left (or none ticked) it
// defaults to the campaign's channels. Content for a hidden channel is never
// deleted elsewhere: this only chooses which platforms the UTM tags apply to.
export function utmPlatformsForChannels(
  current: AdChannel[],
  channels: CampaignChannels | null | undefined,
): AdChannel[] {
  const allowed = channelsList(channels);
  const kept = current.filter((p) => allowed.includes(p));
  return kept.length > 0 ? kept : allowed;
}

// The form's channel change. ONLY `channels` and the UTM platforms move: every
// other field (Meta copy, Google assets, lead forms, folders...) is returned
// untouched, so content for a hidden channel is kept and switching back restores
// it. Generic so the rule is testable without the form.
export function applyChannelChange<
  T extends { channels: CampaignChannels; utmConfig: { platforms: AdChannel[] } },
>(prev: T, channels: CampaignChannels): T {
  return {
    ...prev,
    channels,
    utmConfig: {
      ...prev.utmConfig,
      platforms: utmPlatformsForChannels(prev.utmConfig.platforms, channels),
    },
  };
}
